import { Spacing } from '@persona-id/design-tokens';
import { FC, PropsWithChildren } from 'react';
import styled from 'styled-components';

import Tooltip from 'dashboard/components/Tooltip';
import { formatNumber } from 'dashboard/lib/utils/formatters';
import { MetricsVolumeSummary } from 'dashboard/models/metrics';
import { EMPTY_VOLUME_SUMMARY } from 'dashboard/redux/reducers/metrics-inquiry';
import Card from 'design-system/components/Card';
import { Heading } from 'design-system/components/Text';

// TODO: only show pending and in progress if > 0

const SummaryValueContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: ${Spacing[2]};
  align-items: flex-start;
`;

const SummaryNumber: FC<PropsWithChildren<{ 'data-test'?: string }>> = (props) => (
  <Heading size='sm' {...props} />
);

const Summary: FC<PropsWithChildren<{ label: string }>> = ({ label, children }) => (
  <div>
    <Card.Subtitle>{label}</Card.Subtitle>
    <SummaryValueContainer>{children}</SummaryValueContainer>
  </div>
);

const Container = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(5, auto);
  grid-gap: ${Spacing[2]} ${Spacing[5]};
`;

const SummaryTooltip = styled(Tooltip)`
  margin-right: ${Spacing[2]};
`;

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TooltipDivider = styled.hr`
  border-top: 1px solid ${({ theme }) => theme.color.text.primary};
  margin: ${Spacing[1]} 0;
  width: 100%;
`;

interface InquirySummaryProps {
  volumeSummary?: MetricsVolumeSummary;
}

const InquirySummary = (props: InquirySummaryProps) => {
  const volumeSummary = { ...EMPTY_VOLUME_SUMMARY, ...props.volumeSummary };
  return (
    <Container>
      <Summary label='Created'>
        <SummaryNumber data-test='inquiry-summary__created'>
          {formatNumber(volumeSummary.total)}
        </SummaryNumber>
      </Summary>

      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['started'])} Started</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['total'])} Created</span>
          </TooltipContainer>
        }
      >
        <Summary label='Created ⮕ Started'>
          <SummaryNumber data-test='inquiry-summary__started-rate'>
            {formatNumber(volumeSummary['started-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>
      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['completed'])} Completed</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['started'])} Started</span>
          </TooltipContainer>
        }
      >
        <Summary label='Started ⮕ Completed'>
          <SummaryNumber data-test='inquiry-summary__completed-rate'>
            {formatNumber(volumeSummary['completed-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>
      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['completed-and-needs-review'])} Needs review</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['completed'])} Completed</span>
          </TooltipContainer>
        }
      >
        <Summary label='Completed ⮕ Needs review'>
          <SummaryNumber data-test='inquiry-summary__completed-and-needs-review-rate'>
            {formatNumber(volumeSummary['completed-and-needs-review-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>
      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['needs-review'])} Needs review</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['total'])} Created</span>
          </TooltipContainer>
        }
      >
        <Summary label='Created ⮕ Needs review'>
          <SummaryNumber data-test='inquiry-summary__needs-review-rate'>
            {formatNumber(volumeSummary['needs-review-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>

      <div>{/* Layout div - col 0 */}</div>
      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['pending'])} Not started</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['total'])} Created</span>
          </TooltipContainer>
        }
      >
        <Summary label='Created ⮕ Not started'>
          <SummaryNumber data-test='inquiry-summary__pending-rate'>
            {formatNumber(volumeSummary['pending-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>
      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['failed'])} Failed</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['started'])} Started</span>
          </TooltipContainer>
        }
      >
        <Summary label='Started ⮕ Failed'>
          <SummaryNumber data-test='inquiry-summary__failed-rate'>
            {formatNumber(volumeSummary['failed-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>
      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['completed-and-approved'])} Approved</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['completed'])} Completed</span>
          </TooltipContainer>
        }
      >
        <Summary label='Completed ⮕ Approved'>
          <SummaryNumber data-test='inquiry-summary__completed-and-approved-rate'>
            {formatNumber(volumeSummary['completed-and-approved-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>
      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['approved'])} Approved</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['total'])} Created</span>
          </TooltipContainer>
        }
      >
        <Summary label='Created ⮕ Approved'>
          <SummaryNumber data-test='inquiry-summary__approved-rate'>
            {formatNumber(volumeSummary['approved-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>

      <div>{/* Layout div - col 0 */}</div>
      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['pending-and-expired'])} Expired</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['total'])} Created</span>
          </TooltipContainer>
        }
      >
        <Summary label='Created ⮕ Expired'>
          <SummaryNumber data-test='inquiry-summary__pending-and-expired-rate'>
            {formatNumber(volumeSummary['pending-and-expired-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>
      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['expired'])} Expired</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['started'])} Started</span>
          </TooltipContainer>
        }
      >
        <Summary label='Started ⮕ Expired'>
          <SummaryNumber data-test='inquiry-summary__expired-rate'>
            {formatNumber(volumeSummary['expired-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>
      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['completed-and-declined'])} Declined</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['completed'])} Completed</span>
          </TooltipContainer>
        }
      >
        <Summary label='Completed ⮕ Declined'>
          <SummaryNumber data-test='inquiry-summary__completed-and-declined-rate'>
            {formatNumber(volumeSummary['completed-and-declined-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>
      <SummaryTooltip
        placement='left'
        trigger='hover'
        tooltip={
          <TooltipContainer>
            <span>{formatNumber(volumeSummary['declined'])} Declined</span>
            <TooltipDivider />
            <span>{formatNumber(volumeSummary['total'])} Created</span>
          </TooltipContainer>
        }
      >
        <Summary label='Created ⮕ Declined'>
          <SummaryNumber data-test='inquiry-summary__declined-rate'>
            {formatNumber(volumeSummary['declined-rate'], {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </SummaryNumber>
        </Summary>
      </SummaryTooltip>
    </Container>
  );
};

export default InquirySummary;
